import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

type AppRole = Database['public']['Enums']['app_role'];

interface UserRoleResult {
 role: AppRole;
 isGlobalAdmin: boolean;
 tenantId: string | null;
}

/**
 * Fetches the actual user role from the database with global admin status.
 * This hook provides authoritative role information directly from the database.
 */
export function useActualUserRole(userId?: string) {
 return useQuery({
 queryKey: ['actual-user-role', userId],
 queryFn: async (): Promise<UserRoleResult | null> => {
 if (!userId) return null;
 
 // Get role using the RPC function for consistency with backend
 let effectiveRole: string | null = null;
 
 const { data: roleData, error: roleError } = await supabase
 .rpc('get_user_app_role', { _user_id: userId });
 
 if (roleError) {
 // Fallback: If RPC fails (type mismatch), query directly
 console.warn('RPC get_user_app_role failed, using fallback query:', roleError);
 
 const { data: roleRows } = await supabase
 .from('user_roles')
 .select('role')
 .eq('user_id', userId)
 .order('created_at', { ascending: false })
 .limit(1);
 
 if (roleRows && roleRows.length > 0) {
 effectiveRole = roleRows[0].role;
 }
 } else {
 effectiveRole = roleData;
 }

 // Check global admin status
 const { data: globalAdminData, error: globalAdminError } = await supabase
 .from('global_admin_assignments')
 .select('id')
 .eq('user_id', userId)
 .maybeSingle();

 if (globalAdminError && globalAdminError.code !== 'PGRST116') {
 console.error('Error checking global admin status:', globalAdminError);
 }

 // Get tenant ID from profile
 let tenantId: string | null = null;
 const { data: defaultTenantId, error: defaultTenantError } = await supabase
 .rpc("get_user_default_tenant" as any, { p_user_id: userId } as any);

 if (!defaultTenantError && defaultTenantId) {
 tenantId = defaultTenantId as string;
 } else {
 // Back-compat fallback
 const { data: profileData, error: profileError } = await supabase
 .from('profiles')
 .select('tenant_id')
 .eq('user_id', userId)
 .maybeSingle();

 if (profileError) {
 console.error('Error fetching profile:', profileError);
 }
 tenantId = profileData?.tenant_id || null;
 }

 return {
 role: (effectiveRole as AppRole) || 'sales_rep',
 isGlobalAdmin: !!globalAdminData || effectiveRole === 'global_admin',
 tenantId,
 };
 },
 enabled: !!userId,
 staleTime: 30000, // Cache for 30 seconds
 refetchOnWindowFocus: true,
 });
}
